import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Paging from "./Paging";
import { fetchWithToken } from "../api/apiutils";

const DataTable = ({ filters }) => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const rowsPerPage = 10;
  const navigate = useNavigate();

  const fetchData = async (page) => {
    try {
      setLoading(true);
      console.log(`Fetching data for page ${page}`);
      const response = await fetchWithToken(
        `/api/dashboard?page=${page}&pageLimit=${rowsPerPage}`,
      );
      if (!response.ok) {
        throw new Error("Не удалось получить данные");
      }
      const responseData = await response.json();
      console.log("Fetched Data:", responseData.data);
      setData(responseData.data || []);
      setTotalRecords(responseData.all || 0);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(totalRecords / rowsPerPage);
  }, [totalRecords, rowsPerPage]);

  const filterData = (data) => {
    return data.filter((item) => {
      const matchesStatus =
        !filters.status || item.deactivated === (filters.status === "inactive");
      const matchesLikes =
        !filters.likes ||
        (filters.likes === "0"
          ? item.countOfLikes === 0
          : item.countOfLikes > 0);
      const matchesNameOrId =
        !filters.nameOrId ||
        (item.fullName &&
          item.fullName
            .toLowerCase()
            .includes(filters.nameOrId.toLowerCase())) ||
        (item.userName &&
          item.userName.toLowerCase().includes(filters.nameOrId.toLowerCase()));
      const matchesRegistrationDate =
        !filters.registrationDate ||
        (item.dateOfRegistration &&
          item.dateOfRegistration.startsWith(filters.registrationDate));
      return (
        matchesStatus &&
        matchesLikes &&
        matchesNameOrId &&
        matchesRegistrationDate
      );
    });
  };

  const filteredData = useMemo(() => filterData(data), [data, filters]);

  const handlePageChange = useCallback((page) => {
    console.log("Changing to page:", page);
    setCurrentPage(page);
  }, []);

  const handleFilterClick = useCallback(
    (userId) => {
      navigate(`/filters/${userId}`);
    },
    [navigate],
  );

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Typography color="error">Ошибка: {error}</Typography>;
  }

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Номер телефона</TableCell>
              <TableCell>Дата регистрации</TableCell>
              <TableCell>Подписки</TableCell>
              <TableCell>Лайки</TableCell>
              <TableCell>Дизлайки</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>ФИО</TableCell>
              <TableCell>ИИН/БИН</TableCell>
              <TableCell>Баланс</TableCell>
              <TableCell>Подборки</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length > 0 ? (
              filteredData.map((item, index) => (
                <TableRow key={item.id || index}>
                  <TableCell>
                    {(currentPage - 1) * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell>{item.userName}</TableCell>
                  <TableCell>
                    {new Date(item.dateOfRegistration).toLocaleString("ru-RU", {
                      dateStyle: "short",
                      timeStyle: "short",
                      timeZone: "Asia/Almaty",
                    })}
                  </TableCell>
                  <TableCell>{item.countOfSubs}</TableCell>
                  <TableCell>{item.countOfLikes}</TableCell>
                  <TableCell>{item.countOfDislikes}</TableCell>
                  <TableCell>
                    {item.deactivated ? "Деактивирован" : "Активен"}
                  </TableCell>
                  <TableCell>{item.fullName}</TableCell>
                  <TableCell>{item.identifier || item.companyName}</TableCell>
                  <TableCell>{item.balance}</TableCell>
                  <TableCell>{item.countOfSelection}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="primary"
                      style={{ color: "#ffffff", backgroundColor: "#4298D7" }}
                      onClick={() => handleFilterClick(item.user.id)}
                    >
                      Фильтры
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  Нет данных
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Paging
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </Box>
  );
};

export default DataTable;
